import type { ChatStepSlug } from '@sigora/domain';
import type { ChatTransport, UIMessageChunk } from 'ai';

import { type StartedTurn, startTurn } from '../api/client.ts';
import { readApiError } from '../api/errors.ts';
import { reportUnauthorized } from '../api/unauthorized.ts';
import { type SigoraUIMessage, createFrameMapper } from './events.ts';
import { readSseFrames } from './sse.ts';

/**
 * Where a turn's events come from. Injectable so the transport can be driven from a scripted
 * fixture in a test without a server or a network.
 */
export interface TurnStreamSource {
  start: (message: string | undefined) => Promise<StartedTurn>;
  open: (input: { turnId: string; afterSeq: number; signal: AbortSignal }) => Promise<Response>;
}

/**
 * Turns on one thread: the study and the step whose conversation they are written into.
 *
 * `step` is null on a screen with no conversation, where nothing is drawn to send from; a start
 * there is refused rather than sent to a thread nobody chose. Opening a turn's stream needs no
 * thread, since a turn is addressed by its own id.
 */
export const createHttpTurnStreamSource = (
  studyId: string,
  step: ChatStepSlug | null,
): TurnStreamSource => ({
  start: async (message) => {
    if (step === null) {
      throw new Error('this step has no conversation to send to');
    }

    return startTurn({ studyId, step, message });
  },
  open: async ({ turnId, afterSeq, signal }) => {
    // The cursor goes in the query string rather than the header because this is a plain fetch
    // resuming a turn it already knows about — the header is what a browser sends on its own
    // automatic retry, which is not the mechanism here.
    const query = afterSeq > 0 ? `?lastEventId=${String(afterSeq)}` : '';
    const response = await fetch(`/api/turns/${turnId}/events${query}`, {
      headers: { accept: 'text/event-stream' },
      signal,
    });

    if (!response.ok) {
      // The stream is the longest-lived request the app makes, so it is a likely place for a
      // session to end. Same-origin means the cookie travels with it and nothing here has to
      // attach one; what the app has to know is that it stopped being accepted.
      reportUnauthorized(response.status);

      throw await readApiError(response);
    }

    return response;
  },
});

/** How many times a dropped connection is resumed before the reply is left closed. */
const MAX_RECONNECTS = 5;
const RECONNECT_DELAY_MS = 500;

const delay = async (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    function onAbort(): void {
      clearTimeout(timer);
      resolve();
    }

    signal.addEventListener('abort', onAbort, { once: true });
  });

const lastUserMessage = (messages: readonly SigoraUIMessage[]): SigoraUIMessage | undefined => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === 'user') {
      return message;
    }
  }

  return undefined;
};

const textOf = (message: SigoraUIMessage | undefined): string | undefined => {
  const text = (message?.parts ?? [])
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('')
    .trim();

  return text === '' ? undefined : text;
};

/**
 * The transport, plus the id of the turn it is watching. Stopping is a server-side act — the
 * worker is what is spending — so the UI needs the id to ask for it; aborting the local read
 * only stops the listening.
 */
export type TurnChatTransport = ChatTransport<SigoraUIMessage> & {
  currentTurnId: () => string | undefined;
  /**
   * Points the transport at a turn something other than this chat started — an approval's
   * follow-up, which the server starts and answers with the id — so that the chat's own resume
   * reads it from the beginning. The reply then arrives over the same stream, the same mapping and
   * the same reconciliation as one the user typed for.
   */
  adoptTurn: (turnId: string) => void;
};

/**
 * The chat transport, over the turn spine rather than over a streaming HTTP handler.
 *
 * The shape is the spine's, and it is the reason this is a custom transport rather than the
 * default one: sending a message writes a row and returns an id, and the reply is read from a
 * separate endpoint that replays the log from a cursor and then follows. Nothing about the reply
 * depends on the connection that asked for it, so a dropped socket is recoverable rather than
 * fatal: the read resumes from the last sequence number it forwarded, and the turn never knew.
 */
export const createTurnChatTransport = (
  source: TurnStreamSource,
  {
    onUserMessageStored,
    onAssistantMessageStored,
    onStreamStart,
    onTurnStarted,
  }: {
    /**
     * Told which stored row the message just sent became, so the copy rendered optimistically and
     * the row a transcript read returns can be recognised as one message rather than two. Reported
     * here because this is the only place that sees both ids at once.
     */
    onUserMessageStored?: (ids: { clientId: string; messageId: string }) => void;
    /**
     * Told which stored row a turn's reply became, for the same reconciliation from the other
     * side. Named by turn rather than by the streamed message's id, because the id the chat is
     * rendering the reply under is the SDK's and this layer never sees it.
     */
    onAssistantMessageStored?: (ids: { turnId: string; messageId: string }) => void;
    /**
     * Told that a read of a turn has begun, before any of its events are mapped.
     *
     * What it exists for is the other side of `onAssistantMessageStored`: a reply's stored id
     * arrives one frame before its turn ends and can only be applied once the stream is over, so
     * it is held in between — and anything still held when the *next* stream starts belongs to a
     * turn that is no longer the one on screen. Reported from here rather than left to the
     * consumer's own send and resume paths, because this is the one place every stream passes
     * through, present and future.
     */
    onStreamStart?: (turnId: string) => void;
    /**
     * Told which turn a send started, the moment there is an id for it.
     *
     * A send is one request and a stop is another, so a cancel can be asked for before the turn it
     * has to name exists. Reading the id back later cannot answer that: the id of the *last* turn
     * outlives it, so a cancel in that window would address a turn that has already ended and let
     * the new one run. This is the only moment the pairing is unambiguous.
     */
    onTurnStarted?: (turnId: string) => void;
  } = {},
): TurnChatTransport => {
  let activeTurnId: string | undefined;

  /**
   * How far each turn's reply has been forwarded, keyed by turn.
   *
   * Keyed rather than a single number, because two things need it at once and they are not the
   * same turn. A resume has to know where *its* turn got to, across separate reads — that is what
   * makes a dropped socket free. But a read of the previous turn can still be draining when the
   * next one is adopted, and with one shared number that old read would advance the new turn's
   * resume point using sequence numbers from a different log — so a reconnect would ask for events
   * after a number that means nothing here, and skip the ones in between. A stream only ever reads
   * and writes its own turn's entry, so it cannot move another's.
   */
  const forwardedTo = new Map<string, number>();

  const streamFrom = ({
    turnId,
    afterSeq,
    abortSignal,
  }: {
    turnId: string;
    afterSeq: number;
    abortSignal: AbortSignal | undefined;
  }): ReadableStream<UIMessageChunk> => {
    const controller = new AbortController();
    // Both, and in this order. A listener answers an abort that arrives while the read is running;
    // it never fires for one that arrived before this stream existed, which is what a cancel during
    // the send is — leaving the read to open and run the whole reply in under a mark that says it
    // was cancelled.
    if (abortSignal?.aborted === true) {
      controller.abort();
    }

    abortSignal?.addEventListener('abort', () => controller.abort(), { once: true });

    /**
     * Lets go of the turn this read was following.
     *
     * Once a read is over — finished or cancelled — the transport is watching nothing, and saying
     * otherwise is worse than saying nothing: the id would answer for the *next* turn's cancel and
     * send it at a turn that has already stopped, which the stop endpoint accepts without
     * complaint. Guarded on identity because a read can outlive its turn being replaced: the next
     * turn is already adopted and this one must not take its id away.
     */
    const releaseTurn = (): void => {
      if (activeTurnId === turnId) {
        activeTurnId = undefined;
      }
    };

    onStreamStart?.(turnId);

    const mapper = createFrameMapper({
      textId: `${turnId}-text`,
      onAssistantMessageStored: (messageId) => onAssistantMessageStored?.({ turnId, messageId }),
    });

    return new ReadableStream<UIMessageChunk>({
      start: async (stream) => {
        const emit = (chunks: UIMessageChunk[]): void => {
          for (const chunk of chunks) {
            stream.enqueue(chunk);
          }
        };

        // Its own cursor, resumed from whatever this turn already forwarded — never from what
        // another turn did.
        let cursor = Math.max(forwardedTo.get(turnId) ?? 0, afterSeq);
        forwardedTo.set(turnId, cursor);
        emit(mapper.start());

        try {
          /**
           * Reconnects rather than giving up, which is the whole point of a log the server
           * replays from a cursor: a dropped socket costs nothing, because the events are still
           * there and the turn is still running. Ending the message on the first disconnect
           * would abandon a reply that the worker goes on producing — and paying for.
           *
           * Bounded, because a turn that cannot be reached at all should surface rather than
           * retry silently for as long as the tab is open.
           */
          for (let attempt = 0; attempt <= MAX_RECONNECTS; attempt += 1) {
            const response = await source.open({
              turnId,
              afterSeq: cursor,
              signal: controller.signal,
            });

            if (response.body === null) {
              throw new Error('the turn stream had no body');
            }

            for await (const frame of readSseFrames(response.body, controller.signal)) {
              if (frame.id !== undefined) {
                const seq = Number(frame.id);
                if (Number.isSafeInteger(seq) && seq > cursor) {
                  cursor = seq;
                  forwardedTo.set(turnId, cursor);
                }
              }

              emit(mapper.map(frame));

              if (mapper.isFinished) {
                break;
              }
            }

            if (mapper.isFinished || controller.signal.aborted) {
              break;
            }

            // Resumed from the last sequence number forwarded, so nothing is replayed twice and
            // nothing in the gap is lost.
            await delay(RECONNECT_DELAY_MS, controller.signal);
          }

          emit(mapper.end());

          if (mapper.isFinished) {
            // A finished turn is never resumed, so its cursor is dead weight; dropping it also
            // keeps a long session from accumulating one entry per turn.
            forwardedTo.delete(turnId);
          }

          stream.close();
        } catch (error: unknown) {
          if (controller.signal.aborted) {
            emit(mapper.end());
            stream.close();
            return;
          }

          stream.error(error);
        } finally {
          // Every way out above is the read ending: a terminal frame, a cancel, reconnects spent
          // without either, or a failure nobody can retry. The transport is watching nothing in all
          // four, and the two with nothing to show are where saying otherwise costs most — the
          // researcher's next move is to send again, and an id left behind takes that turn's cancel
          // and spends it on this one, which the stop endpoint accepts without a word.
          releaseTurn();
        }
      },
      cancel: () => {
        controller.abort();
      },
    });
  };

  return {
    currentTurnId: () => activeTurnId,

    adoptTurn: (turnId) => {
      activeTurnId = turnId;
    },

    sendMessages: async ({ messages, abortSignal, trigger }) => {
      // Regeneration would need the agent to be told which message to redo, which is the agent
      // runtime's contract to define. Refusing is better than silently sending the last message
      // again, which would look like it worked and quietly duplicate a turn.
      if (trigger === 'regenerate-message') {
        throw new Error('Ask again in your own words — replies cannot be regenerated yet.');
      }

      const sent = lastUserMessage(messages);
      const { turnId, messageId } = await source.start(textOf(sent));

      if (sent !== undefined && messageId !== undefined) {
        onUserMessageStored?.({ clientId: sent.id, messageId });
      }

      activeTurnId = turnId;
      onTurnStarted?.(turnId);

      return streamFrom({ turnId, afterSeq: 0, abortSignal });
    },

    reconnectToStream: async () => {
      if (activeTurnId === undefined) {
        return null;
      }

      return streamFrom({
        turnId: activeTurnId,
        afterSeq: forwardedTo.get(activeTurnId) ?? 0,
        abortSignal: undefined,
      });
    },
  };
};
